import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Sparkles, Target, FileText, Video, CreditCard, LogOut, LogIn } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Logo from "../images/thumbnail.png";

// Data for services - remains the same
const services = [
  {
    icon: <Sparkles className="w-5 h-5" />,
    title: "Brainstorm Idea",
    path: "/ideate",
    color: "blue",
    description: "Find unmet needs & pain points",
  },
  {
    icon: <FileText className="w-5 h-5" />,
    title: "Business Plan",
    // path: "/report",
    path: "/dynamicreports/",
    color: "green",
    description: "100+ page comprehensive plan",
  },
  {
    icon: <Target className="w-5 h-5" />,
    title: "Buyer Intent Search",
    path: "/leadfinder",
    color: "purple",
    description: "Find ready-to-buy customers",
  },
  {
    icon: <Video className="w-5 h-5" />,
    title: "TikTok & Reels",
    path: "/video",
    color: "orange",
    description: "Automate social content",
  },
];

// Tailwind class mapping for consistent hover/focus states - remains the same
const colorClasses = {
  blue: {
    text: "text-blue-600",
    hoverText: "hover:text-blue-700",
    hoverBg: "hover:bg-blue-50",
    focusRing: "focus:ring-blue-500",
    iconBg: "bg-blue-100",
    iconText: "text-blue-700",
  },
  green: {
    text: "text-green-600",
    hoverText: "hover:text-green-700",
    hoverBg: "hover:bg-green-50",
    focusRing: "focus:ring-green-500",
    iconBg: "bg-green-100",
    iconText: "text-green-700",
  },
  purple: {
    text: "text-purple-600",
    hoverText: "hover:text-purple-700",
    hoverBg: "hover:bg-purple-50",
    focusRing: "focus:ring-purple-500",
    iconBg: "bg-purple-100",
    iconText: "text-purple-700",
  },
  orange: {
    text: "text-orange-600",
    hoverText: "hover:text-orange-700",
    hoverBg: "hover:bg-orange-50",
    focusRing: "focus:ring-orange-500",
    iconBg: "bg-orange-100",
    iconText: "text-orange-700",
  },
};

// Renamed component for clarity
const EnhancedHeader = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const isHomePage = location.pathname === "/" || location.pathname === "";

  useEffect(() => {
    // Check if user is logged in by looking for access token
    const token = localStorage.getItem("access");
    setIsLoggedIn(!!token);
  }, [location]); // Re-check when location changes

  const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);
  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  const handleLogout = () => {
    localStorage.removeItem("access");
    setIsLoggedIn(false);
    navigate("/");
    closeMobileMenu();
  };

  // Style for the Credits link (similar to old Dashboard link)
  const creditsLinkStyle =
    "flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-emerald-700 hover:bg-emerald-50 transition-colors duration-200 rounded-md px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2";

  const mobileCreditsLinkStyle =
    "flex items-center gap-3 px-4 py-3 bg-gray-50 text-gray-700 rounded-lg font-medium transition-colors duration-200 hover:bg-emerald-100 hover:text-emerald-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500";

  // Auth button styles
  const authButtonStyle =
    "flex items-center gap-1.5 text-sm font-medium transition-all duration-200 rounded-md px-3 py-1.5 outline-none focus:outline-none focus:ring-2 focus:ring-offset-2";

  const loginButtonStyle = `${authButtonStyle} text-green-600 hover:text-green-700 hover:bg-green-50 focus:ring-green-500`;
  const logoutButtonStyle = `${authButtonStyle} text-green-600 hover:text-green-700 hover:bg-green-50 focus:ring-green-500`;

  const mobileAuthButtonStyle =
    "flex items-center gap-3 px-4 py-3 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2";

  const mobileLoginButtonStyle = `${mobileAuthButtonStyle} bg-green-50 text-green-700 hover:bg-green-100 focus:ring-green-500`;
  const mobileLogoutButtonStyle = `${mobileAuthButtonStyle} bg-red-50 text-red-700 hover:bg-red-100 focus:ring-red-500`;

  return (
    <header className="h-16 bg-white/90 backdrop-blur-lg border-b border-gray-200 shadow-sm sticky top-0 z-50 transition-shadow duration-300 hover:shadow-md">
      <div className="max-w-7xl w-full mx-auto flex justify-between items-center h-full px-4 sm:px-6 lg:px-8">
        {/* Logo - Green, links to /dashboard */}
        {/* Site branding */}
        <div className="flex-shrink-0">
          <Link to="/dashboard" className="flex items-center">
            <img src={Logo} alt="FeedbackbyAI Logo" className="h-10" />
            <span className="ml-3 text-2xl font-bold text-green-600">FeedbackbyAI</span>
          </Link>
        </div>
        {/* Desktop Navigation - Services + Credits */}
        <nav className="hidden md:flex items-center space-x-5 lg:space-x-6">
          {isLoggedIn &&
            /* Service Links with Icons - Only show when logged in */
            services.map((service) => (
              <Link
                key={service.path}
                to={service.path}
                className={`flex items-center gap-1.5 text-sm font-medium text-gray-600 transition-all duration-200 rounded-md px-3 py-1.5 outline-none focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                  colorClasses[service.color].hoverText
                } ${colorClasses[service.color].hoverBg} ${colorClasses[service.color].focusRing}`}
              >
                {React.cloneElement(service.icon, { className: `w-4 h-4 ${colorClasses[service.color].text}` })}
                {service.title}
              </Link>
            ))}
          {isLoggedIn && (
            /* Credits/Recharge Link with Icon - Only show when logged in */
            <Link to="/credits" className={creditsLinkStyle}>
              <CreditCard className="h-4 w-4" />
              Credits/Recharge
            </Link>
          )}
          {/* Auth button - Login or Logout based on state */}

          {isHomePage ? (
            // On homepage, show Sign In or Logout based on login status
            isLoggedIn ? (
              <button onClick={handleLogout} className={logoutButtonStyle}>
                <LogOut className="h-4 w-4" />
                Logout
              </button>
            ) : (
              <Link to="/signin" className={loginButtonStyle}>
                <LogIn className="h-4 w-4" />
                Sign In
              </Link>
            )
          ) : (
            // On other pages, always show Logout (since user should be logged in)
            <button onClick={handleLogout} className={logoutButtonStyle}>
              <LogOut className="h-4 w-4" />
              Logout
            </button>
          )}
        </nav>
        {/* Mobile Menu Button */}
        <button
          className="md:hidden p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-inset focus:ring-emerald-500"
          onClick={toggleMobileMenu}
          aria-label="Toggle menu"
          aria-expanded={isMobileMenuOpen}
        >
          {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>
      {/* Mobile Navigation Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="md:hidden absolute top-16 left-0 right-0 bg-white border-b border-gray-200 shadow-lg z-40"
          >
            <nav className="max-w-7xl mx-auto px-4 pt-4 pb-6 space-y-2">
              {isLoggedIn &&
                /* Mobile Service Links with Icons - Only when logged in */
                services.map((service) => (
                  <Link
                    key={service.path}
                    to={service.path}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors duration-200 ${
                      colorClasses[service.color].hoverBg
                    } focus:outline-none focus:ring-2 focus:ring-offset-2 ${colorClasses[service.color].focusRing} group`}
                    onClick={closeMobileMenu}
                  >
                    <div className={`p-2 rounded-lg ${colorClasses[service.color].iconBg}`}>
                      {React.cloneElement(service.icon, { className: `w-5 h-5 ${colorClasses[service.color].iconText}` })}
                    </div>
                    <div>
                      <p className={`font-medium ${colorClasses[service.color].text} group-hover:${colorClasses[service.color].hoverText}`}>
                        {service.title}
                      </p>
                      <p className="text-xs text-gray-500">{service.description}</p>
                    </div>
                  </Link>
                ))}

              {isLoggedIn && (
                /* Mobile Credits/Recharge Link with Icon - Only when logged in */
                <Link to="/credits" className={mobileCreditsLinkStyle} onClick={closeMobileMenu}>
                  <CreditCard className="w-5 h-5" />
                  Credits/Recharge
                </Link>
              )}

              {/* Mobile Auth button - Login or Logout based on state */}
              {isLoggedIn ? (
                <button onClick={handleLogout} className={mobileLogoutButtonStyle}>
                  <LogOut className="w-5 h-5" />
                  Logout
                </button>
              ) : (
                <Link to="/signin" className={mobileLoginButtonStyle} onClick={closeMobileMenu}>
                  <LogIn className="w-5 h-5" />
                  Sign In
                </Link>
              )}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default EnhancedHeader;
